// ./src/contexts/UserInvitationsContext.tsx
//
// Context wrapper around the useUserInvitations hook.
//
// Purpose:
// - Allows sibling components (list, edit drawer, toolbar)
//   to share the same invitations state without prop-drilling.
// - Each page creates its own <UserInvitationsProvider> with its own config,
//   so state doesn't leak across pages.
// - The underlying useUserInvitations hook remains the data engine; this context
//   simply makes its return value available to the component tree.
//
// Usage:
//
//   // In a page component:
//   <UserInvitationsProvider autoLoad={false} pageSize={20}>
//     <UserInvitationsPageContent />
//     <UserInvitationEditDrawer />
//   </UserInvitationsProvider>
//
//   // In any child component:
//   const { userInvitations, loading, create, ... } = useUserInvitationsContext();
//
//   // In a component that may or may not be inside a provider:
//   const context = useOptionalUserInvitationsContext();
//   const updateFn = context?.update ?? props.onUpdate;

import React, { createContext, useContext, useMemo } from "react";

import {
  useUserInvitations,
  UseUserInvitationsOptions,
  UseUserInvitationsReturn,
} from "@/hooks/useUserInvitations";

// ============================================
// Context
// ============================================

const UserInvitationsContext = createContext<UseUserInvitationsReturn | null>(null);

// ============================================
// Provider
// ============================================

export interface UserInvitationsProviderProps extends UseUserInvitationsOptions {
  children: React.ReactNode;
}

/**
 * Wraps useUserInvitations into a context provider.
 *
 * Each instance creates its own independent store with its own
 * filters, pagination, loading state, and error state.
 *
 * The provider value is defensively memoized so that even if
 * useUserInvitations is refactored to return a non-memoized object
 * in the future, context consumers won't receive unnecessary re-renders.
 */
export const UserInvitationsProvider: React.FC<UserInvitationsProviderProps> = ({
  children,
  ...options
}) => {
  const invitationsData = useUserInvitations(options);

  const value = useMemo(() => invitationsData, [invitationsData]);

  return (
    <UserInvitationsContext.Provider value={value}>
      {children}
    </UserInvitationsContext.Provider>
  );
};

// ============================================
// Hooks
// ============================================

/**
 * Access the UserInvitationsContext. Throws if used outside a provider.
 *
 * Use this in components that are always rendered inside a provider
 * (e.g. UserInvitationsList, toolbar).
 */
export const useUserInvitationsContext = (): UseUserInvitationsReturn => {
  const context = useContext(UserInvitationsContext);

  if (!context) {
    throw new Error(
      "useUserInvitationsContext must be used within a <UserInvitationsProvider>. " +
      "Wrap your component tree with <UserInvitationsProvider> or use useOptionalUserInvitationsContext() instead."
    );
  }

  return context;
};

/**
 * Access the UserInvitationsContext if available, or return null.
 *
 * Use this in components that may or may not be inside a provider
 * (e.g. UserInvitationEditDrawer which could work both inside the
 * page's provider and standalone elsewhere).
 */
export const useOptionalUserInvitationsContext = (): UseUserInvitationsReturn | null => {
  return useContext(UserInvitationsContext);
};